import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import Waypoint from 'react-waypoint';
import debounce from 'lodash/debounce';
import { generatePath } from 'react-router';

import withProfile from 'hocs/withProfile';

import {
  JORNALIST,
  EDITOR,
  CORRECTOR,
  SUPERVISOR,
  BILLER,
  MODERATOR,
} from 'utils/roles';

import Posts from 'api/resources/posts';
import getErrors from 'utils/getErrors';
import { Deserializer } from 'jsonapi-serializer';

import ReconnectingWebSocket from 'containers/ReconnectingWebSocket';
import PublicationRow from 'admin-editor/components/PublicationRow';
import PublicationRowStub from 'admin-editor/components/PublicationRowStub';

import FiltersDropdown from 'components/FiltersDropdown';
import SearchForm from 'components/SearchForm';
import MaterialNotFound from 'components/MaterialNotFound';

import ErrorText from 'styled/Page/ErrorText';
import PrimaryLink from 'styled/PrimaryLink';
import Main from 'styled/Page/Main';
import CenteredContainer from 'styled/Page/CenteredContainer';
import RightColumn from 'styled/Page/RightColumn';
import FilterWrapper from 'styled/Page/FilterWrapper';
import PublicationsList from './PublicationsList';

const PAGE_SIZE = 15;

const SORT_FILTERS = [
  {
    label: <span>&darr; По дате создания</span>,
    value: '-created_at',
  }, {
    label: <span>&uarr; По дате создания</span>,
    value: 'created_at',
  }, {
    label: <span>&darr; По дате публикации</span>,
    value: '-published_at',
  }, {
    label: <span>&uarr; По дате публикации</span>,
    value: 'published_at',
  },
];

const POST_TYPE_FILTERS = [
  {
    label: 'Все типы публикаций',
    value: 'all',
  }, {
    label: 'iStory',
    value: 'iStory',
  }, {
    label: 'Короткие посты',
    value: 'short',
  }, {
    label: 'Лонгриды',
    value: 'longread',
  },
];

const STATE_FILTERS = [
  {
    label: 'Все статусы',
    value: 'all',
  }, {
    label: 'Черновик',
    value: 'draft',
  }, {
    label: 'В обработке',
    value: 'processing',
  }, {
    label: 'Опубликовано',
    value: 'published',
  }, {
    label: 'Скрыта',
    value: 'hidden',
  },
];

export class PublicationsPage extends React.PureComponent {
  state = {
    loading: false,
    publications: [],
    hasMore: false,
    pageNumber: 0,
    errors: [],
    sort: SORT_FILTERS.find(item => item.value === this.props.match.params.sort),
    postType: POST_TYPE_FILTERS.find(item => item.value === this.props.match.params.post_type),
    state: STATE_FILTERS.find(item => item.value === this.props.match.params.state),
    searchQuery: undefined,
  }

  loadPublicationsPostsWithDebounce = debounce(() => {
    this.loadPublications();
  }, 500);

  componentDidMount() {
    this.loadPublications();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.profile.roles.length !== this.props.profile.roles.length) {
      this.reloadPublications();
    }

    if (this.props.match.params !== prevProps.match.params) {
      this.setState({ // eslint-disable-line react/no-did-update-set-state
        sort: SORT_FILTERS.find(item => item.value === this.props.match.params.sort),
        postType: POST_TYPE_FILTERS.find(item => item.value === this.props.match.params.post_type),
        state: STATE_FILTERS.find(item => item.value === this.props.match.params.state),
      }, this.reloadPublications);
    }
  }

  get filterState() {
    return STATE_FILTERS.filter(filter => this.props.can({
      resource: 'publications',
      permission: 'filterStateChange',
      state: filter.value,
    }));
  }

  handleAdminNotificationsMessage = async (response) => {
    if (!response || !response.body) return;

    if (response.type !== 'post') return;
    if (!['lock', 'unlock'].includes(response.action)) return;

    this.setState(prevState => ({
      publications: prevState.publications.map(publication => (
        publication.id !== response.body.id ? publication : ({
          ...publication,
          locked: response.body.locked,
          lockedEmployeeFullname: response.body.locked_employee_fullname,
          lockedEmployeeId: response.body.locked_employee_id,
        })
      )),
    }));
  }

  handleSortFilterChange = (sort = {}) => {
    this.redirectToFilterPath({ sort: sort.value });
  }

  handleStateFilterChange = (state = {}) => {
    this.redirectToFilterPath({ state: state.value });
  }

  handlePostTypeFilterChange = (postType = {}) => {
    this.redirectToFilterPath({ post_type: postType.value });
  }

  redirectToFilterPath = (params = {}) => {
    this.props.history.push(
      generatePath(
        '/publications/post_type/:post_type/state/:state/sort/:sort',
        {
          ...this.props.match.params,
          ...params,
        }
      )
    );
  }

  handleSearchChange = ({ target }) => {
    this.setState({
      searchQuery: target.value,
      pageNumber: 0,
      publications: [],
    }, this.loadPublicationsPostsWithDebounce);
  }

  handleSearchClose = () => {
    this.setState({
      searchQuery: undefined,
      pageNumber: 0,
      publications: [],
    }, this.loadPublications);
  }

  handleSearchSubmit = (e) => {
    e.preventDefault();
    this.loadPublications();
  }

  reloadPublications = () => {
    this.setState({
      pageNumber: 0,
      publications: [],
    }, this.loadPublications);
  }

  loadPublications = async () => {
    this.setState({ loading: true, errors: [] });

    try {
      const response = await Posts.getAll({
        page: {
          number: this.state.pageNumber,
          size: PAGE_SIZE,
        },
        sort: this.props.match.params.sort,
        filter: {
          post_type: this.props.match.params.post_type === 'all' ? undefined : this.props.match.params.post_type,
          state: this.props.match.params.state === 'all' ? undefined : this.props.match.params.state,
          q: this.state.searchQuery,
        },
        fields: {
          post: 'image,employee,employee.avatar,title,is_editor_approved,is_corrector_approved,published_at,updated_at,created_at,state,locked,locked_employee_id,locked_employee_fullname',
          image: 'url',
          employee: 'first_name,last_name,avatar',
        },
        include: 'image,employee,employee.avatar',
      });

      const publications = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      this.setState(prevState => ({
        publications: prevState.publications.concat(publications),
        loading: false,
        hasMore: publications.length === PAGE_SIZE,
        pageNumber: prevState.pageNumber + 1,
      }));
    } catch (err) {
      const errors = await getErrors(err);
      this.setState({ loading: false, errors });
    }
  }

  render() {
    return (
      <Main>
        <Helmet><title>Публикации</title></Helmet>

        <ReconnectingWebSocket onMessage={this.handleAdminNotificationsMessage} path="/_ws/c/admin/notifications" />

        <div style={{ width: '100%' }}>
          <FilterWrapper style={{ margin: '0 32px' }}>
            <FiltersDropdown
              filters={SORT_FILTERS}
              onFilterChange={this.handleSortFilterChange}
              style={{ marginRight: '24px' }}
              activeFilter={this.state.sort}
            />

            <FiltersDropdown
              filters={POST_TYPE_FILTERS}
              onFilterChange={this.handlePostTypeFilterChange}
              style={{ marginRight: '24px' }}
              activeFilter={this.state.postType}
            />

            <FiltersDropdown
              filters={this.filterState}
              onFilterChange={this.handleStateFilterChange}
              style={{ marginRight: 'auto' }}
              activeFilter={this.state.state}
            />

            <SearchForm
              value={this.state.searchQuery}
              placeholder="Поиск по публикациям"
              onChange={this.handleSearchChange}
              onSubmit={this.handleSearchSubmit}
              onClose={this.handleSearchClose}
            />

            {this.props.can({
              permission: 'create',
              roles: this.props.profile.roles,
              resource: 'publications',
            }) && (
              <PrimaryLink
                to="/publications/new"
                style={{ marginLeft: '20px' }}
              >
                добавить публикацию
              </PrimaryLink>
            )}
          </FilterWrapper>

          <PublicationsList>
            {this.state.publications.map(publication => (
              <PublicationRow
                key={publication.id}
                id={publication.id}
                title={publication.title}
                isEditorApproved={publication.isEditorApproved}
                isCorrectorApproved={publication.isCorrectorApproved}
                author={publication.employee}
                publishedAt={publication.publishedAt * 1000}
                updatedAt={publication.updatedAt * 1000}
                createdAt={publication.createdAt * 1000}
                mainImage={publication.image && publication.image.url}
                state={publication.state}
                locked={publication.locked}
                lockedEmployeeFullname={publication.lockedEmployeeFullname}
                lockedEmployeeId={publication.lockedEmployeeId}
              />
            ))}

            {this.state.loading && (
              Array.from({ length: PAGE_SIZE }, () => <PublicationRowStub key={Math.random()} />)
            )}

            {this.state.errors.length > 0 && (
              <CenteredContainer>
                {this.state.errors.map(error => (
                  <ErrorText role="alert" key={error}>{error}</ErrorText>
                ))}
              </CenteredContainer>
            )}

            {this.state.hasMore && (
              <Waypoint
                key={this.state.pageNumber}
                onEnter={this.loadPublications}
              />
            )}
          </PublicationsList>

          {!this.state.loading &&
            this.state.publications.length === 0 &&
            this.state.errors.length === 0 &&
            <MaterialNotFound text="Публикаций не найдено" />
          }
        </div>

        <RightColumn />
      </Main>
    );
  }
}

PublicationsPage.propTypes = {
  profile: PropTypes.shape({
    roles: PropTypes.arrayOf(
      PropTypes.oneOf([EDITOR, SUPERVISOR, CORRECTOR, JORNALIST, BILLER, MODERATOR])
    ),
  }),
  can: PropTypes.func,
  match: PropTypes.shape({
    params: PropTypes.shape({
      post_type: PropTypes.oneOf(['all', 'iStory', 'short', 'longread']),
      state: PropTypes.oneOf(['all', 'draft', 'processing', 'published', 'hidden']),
      sort: PropTypes.oneOf(['created_at', '-created_at', 'published_at', '-published_at']),
    }),
  }),
  history: PropTypes.shape({ push: PropTypes.func }),
};

PublicationsPage.defaultProps = {
  profile: { roles: [] },
  can: () => {},
  match: { params: {} },
  history: { push: () => {} },
};

export default withProfile(PublicationsPage);
